import React, { useState } from "react";
import { View, StyleSheet, ScrollView, TouchableOpacity, Text, KeyboardAvoidingView, Platform } from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { Card } from "@/components/ui/card";
import DocumentsForm from "@/components/settings/documents-form";
import YourDetailsForm from "@/components/settings/your-details-form";
import SecurityForm from "@/components/settings/security-form";
import AppAccessForm from "@/components/settings/app-access-form";
import colors from "@/constants/Colors";
import { Ionicons } from '@expo/vector-icons';
import { DrawerToggleButton } from "@react-navigation/drawer";
import Drawer from "expo-router/drawer";
import { useCrew } from "@/context/CrewProvider";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { trpc } from "@/trpc/react";
import { Button } from "@/components/ui";
import { useForm } from "react-hook-form";

type TabType = "details" | "security" | "documents" | "access";

const isWeb = Platform.OS === "web";

// Define the shape of a pending change to match the form components
interface PendingChange {
  id: number;
  crew_id: number;
  key: string;
  value: string;
  status: number;
}

export default function Settings() {
  const params = useLocalSearchParams<{ tab?: string; promptEnable?: string }>();
  const initialTab: TabType =
    params.tab === "security" || params.tab === "documents" || params.tab === "access"
      ? params.tab
      : "details";
  const [tab, setTab] = useState<TabType>(initialTab);
  const [autoOpenBiometricEnable, setAutoOpenBiometricEnable] = useState<boolean>(
    params.promptEnable === "1" && initialTab === "access"
  );
  const { currentCrewMember } = useCrew();
  const insets = useSafeAreaInsets();
  const { data: rawPendingChanges, refetch, isLoading } = trpc.crew.getPendingCrewUpdates.useQuery(currentCrewMember!.id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Process the pending changes to ensure they match the expected format
  const pendingChanges = React.useMemo(() => {
    if (!rawPendingChanges) return [];

    return rawPendingChanges
      .filter(change => change.key !== null && change.value !== null)
      .map(change => ({
        id: change.id,
        crew_id: change.crew_id,
        key: change.key as string,
        value: change.value as string,
        status: change.status ?? 0
      }));
  }, [rawPendingChanges]);

  // Track form changes
  const [detailsFormChanged, setDetailsFormChanged] = useState(false);
  const [securityFormChanged, setSecurityFormChanged] = useState(false);

  // References to form submission functions
  const detailsFormRef = React.useRef<{ submit: () => void } | null>(null);
  const securityFormRef = React.useRef<{ submit: () => void } | null>(null);

  const handleFormSubmitSuccess = () => {
    refetch();
    setIsSubmitting(false);

    // Reset form change state after submission
    if (tab === "details") {
      setDetailsFormChanged(false);
    } else if (tab === "security") {
      setSecurityFormChanged(false);
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    if (tab === "details" && detailsFormRef.current) {
      detailsFormRef.current.submit();
    } else if (tab === "security" && securityFormRef.current) {
      securityFormRef.current.submit();
    }
  };

  // Determine if update button should be disabled
  const isUpdateButtonDisabled = () => {
    if (isSubmitting) return true;
    if (tab === "details") return !detailsFormChanged;
    if (tab === "security") return !securityFormChanged;
    return true;
  };

  // Handle form change notifications
  const handleDetailsFormChange = (hasChanges: boolean) => {
    setDetailsFormChanged(hasChanges);
  };

  const handleSecurityFormChange = (hasChanges: boolean) => {
    setSecurityFormChanged(hasChanges);
  };

  useRefetchOnFocus(refetch);

  const showUpdateButton = tab === "details" || tab === "security";

  return (
    <SafeAreaView style={styles.container} edges={["left", "right"]}>
      <Drawer.Screen
        options={{
          drawerLabel: 'Settings',
          title: 'Settings',
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
          headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />
        }}
      />

      {/* Pending Changes Notice */}
      {pendingChanges.length > 0 ? (
        <View style={styles.pendingNoticeContainer}>
          <View style={styles.pendingNotice}>
            <Ionicons name="alert-circle" size={22} color="#FF6B6B" />
            <Text style={styles.pendingText}>
              You have {pendingChanges.length} {pendingChanges.length === 1 ? 'change' : 'changes'} pending ops team review. We'll update your profile as soon as possible.
            </Text>
          </View>
        </View>
      ) : (
        <View style={styles.emptySpaceContainer} />
      )}

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, tab === "details" && styles.activeTab]}
          onPress={() => setTab("details")}
        >
          <Ionicons
            name={tab === "details" ? "person" : "person-outline"}
            size={18}
            color={tab === "details" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "details" && styles.activeTabText]}>Profile</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, tab === "security" && styles.activeTab]}
          onPress={() => setTab("security")}
        >
          <Ionicons
            name={tab === "security" ? "shield-checkmark" : "shield-checkmark-outline"}
            size={18}
            color={tab === "security" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "security" && styles.activeTabText]}>ID</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, tab === "documents" && styles.activeTab]}
          onPress={() => setTab("documents")}
        >
          <Ionicons
            name={tab === "documents" ? "document-text" : "document-text-outline"}
            size={18}
            color={tab === "documents" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "documents" && styles.activeTabText]}>Docs</Text>
        </TouchableOpacity>

        {!isWeb && (
          <TouchableOpacity
            style={[styles.tab, tab === "access" && styles.activeTab]}
            onPress={() => setTab("access")}
          >
            <Ionicons
              name={tab === "access" ? "lock-closed" : "lock-closed-outline"}
              size={18}
              color={tab === "access" ? colors.accent : "#666"}
            />
            <Text style={[styles.tabText, tab === "access" && styles.activeTabText]}>Access</Text>
          </TouchableOpacity>
        )}
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
        keyboardVerticalOffset={100}
      >
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={[styles.formContainer, showUpdateButton && { paddingBottom: 80 + insets.bottom }]}>
            {tab === "details" && <YourDetailsForm
              ref={detailsFormRef}
              onFormSuccess={handleFormSubmitSuccess}
              onFormChange={handleDetailsFormChange}
              pendingChanges={pendingChanges}
            />}
            {tab === "security" && <SecurityForm
              ref={securityFormRef}
              onFormSuccess={handleFormSubmitSuccess}
              onFormChange={handleSecurityFormChange}
              pendingChanges={pendingChanges}
            />}
            {tab === "documents" && <DocumentsForm onFormSuccess={handleFormSubmitSuccess} />}
            {tab === "access" && (
              <AppAccessForm
                autoOpenEnable={autoOpenBiometricEnable}
                onConsumedAutoOpen={() => setAutoOpenBiometricEnable(false)}
              />
            )}
          </View>
        </ScrollView>

        {showUpdateButton && (
          <View style={[styles.fixedButtonContainer, { paddingBottom: insets.bottom + 12 }]}>
            <Button
              size="default"
              variant="crew"
              onPress={handleSubmit}
              style={[styles.submitButton, isUpdateButtonDisabled() && styles.disabledButton]}
              disabled={isUpdateButtonDisabled()}
            >
              {isSubmitting ? (
                <View style={styles.buttonContent}>
                  <Text style={styles.buttonText}>
                    {tab === "details" ? "Updating Details" : "Updating Security Info"}
                  </Text>
                </View>
              ) : (
                <View style={styles.buttonContent}>
                  <Text style={styles.buttonText}>
                    {tab === "details" ? "Update Details" : "Update Security Info"}
                  </Text>
                </View>
              )}
            </Button>
          </View>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  keyboardAvoid: {
    flex: 1,
  },
  tabContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#fff',
    paddingVertical: 4,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 12,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  activeTab: {
    backgroundColor: '#222',
  },
  tabText: {
    marginLeft: 4,
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: colors.accent,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  formContainer: {
    paddingBottom: 24,
  },
  pendingNoticeContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 0,
  },
  pendingNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: '#FF6B6B',
    borderRadius: 10,
    backgroundColor: '#FFF5F5',
  },
  pendingText: {
    flex: 1,
    marginLeft: 12,
    fontSize: 14,
    fontWeight: '500',
    color: '#FF6B6B',
    flexWrap: 'wrap',
  },
  fixedButtonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 12,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  submitButton: {
    borderRadius: 8,
    height: 38,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFF000',
    textTransform: 'uppercase',
  },
  disabledButton: {
    opacity: 0.5,
  },
  emptySpaceContainer: {
    height: 24,
  },
});